import styles from './input.module.scss';
import { Label } from '../Label';
import { Button } from '../Button';
import { IInput, ITagsInput } from '../../types/Input/input';
import { TagsInput } from './TagsInput';

import {
  CircleCheckIcon,
  OpenedEyeIcon,
  ClosedEyeIcon,
  LockIcon,
} from '../../assets/Icons';
import { forwardRef, useState } from 'react';

const localizedTexts = {
  en: {
    forgetPasswordText: 'Forgot password?',
  },
  ar: {
    forgetPasswordText: 'نسيت كلمة السر؟',
  },
};

const isMatch = ({
  value = '',
  pattern,
}: {
  value?: string;
  pattern?: RegExp;
}) => pattern?.test(value);

const isTagsInput = (props: IInput): props is ITagsInput => {
  return props.type === 'tags';
};

export const Input = forwardRef<HTMLInputElement, IInput>((props, ref) => {
  const {
    lang = 'en',
    id,
    name,
    value,
    label,
    labelText,
    hintText,
    required,
    placeholder,
    type = 'text',
    status = 'default',
    disabled,
    readOnly,
    autoFocus = false,
    leadingIcon,
    rules,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    forgetPasswordHandler,
    forgetPasswordText = localizedTexts[lang].forgetPasswordText,
    error,
    extra,
    preventAutoComplete,
    leadingText,
    trailingIcon
  } = props;

  const [showPassword, setShowPassword] = useState(false);

  const handleShowPassword = () => setShowPassword((prevShow) => !prevShow);

  const renderInput = () => {
    if (isTagsInput(props)) {
      return <TagsInput ref={ref} {...props} />;
    }

    return (
      <>
        <input
          id={id}
          name={name}
          value={value}
          className={styles['container__wrapper__input']}
          placeholder={placeholder}
          type={showPassword ? 'text' : type}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          required={required}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          ref={ref}
          autoComplete={preventAutoComplete ? 'one-time-code' : 'on'}
        />
        {type === 'password' &&
          <span
            className={styles['container__wrapper__trailing-icon']}
            onClick={handleShowPassword}
          >
            {!showPassword && OpenedEyeIcon}
            {showPassword && ClosedEyeIcon}
          </span>}
      </>
    );
  };

  return (
    <div
      className={styles['container']}
      data-input-status={status}
    >
      {(label || labelText) && <Label text={label} hintText={labelText} inputName={id} />}

      <div className={styles['container__wrapper']}>
        {leadingIcon && !leadingText && type !== 'password' &&
          <span className={styles['container__wrapper__leading-icon']}>
            {leadingIcon}
          </span>
        }
        {type === 'password' &&
          <span className={styles['container__wrapper__leading-icon']}>
            {LockIcon}
          </span>
        }
        {
          leadingText &&
          <div className={styles['container__wrapper__leading-text']}>
            {leadingText}
          </div>
        }
        {renderInput()}
        {trailingIcon &&
          <span className={styles['container__wrapper__trailing-icon']}>
            {trailingIcon}
          </span>
        }
      </div>

      {hintText && (
        <span className={styles['container__hintText']}>{hintText}</span>
      )}

      {error && (
        <span className={styles['container__hintText']}>{error}</span>
      )}

      {type === 'password' && forgetPasswordHandler &&
        <div className={styles['container__forget-password']}>
          <Button size='sm' color='primary' variant='link' text={forgetPasswordText} onClickHandler={forgetPasswordHandler} />
        </div>
      }

      {type === 'password' && rules && (
        <div className={styles['container__rules']}>
          {rules?.map((rule) => (
            <div
              key={rule.label}
              className={styles['container__rules__rule']}
              data-input-rule={
                !value
                  ? 'default'
                  : isMatch({ value, pattern: rule.pattern })
                    ? 'success'
                    : 'default'
              }
            >
              {CircleCheckIcon}
              <span>{rule.label}</span>
            </div>
          ))}
        </div>
      )}

      {extra}
    </div>
  );
}
);
